import React, { useState } from 'react';
import { Avatar, Button, IconButton, Tooltip } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import ThreeSixtyIcon from '@mui/icons-material/ThreeSixty';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { ItemDTO, OfferDTO } from '../../../user/on-sale/types';
import { RARITY_COLORS, RarityGrade } from '../../../utils/RarityColors';
import ItemScreenshotModal from '../../../items/ItemScreenshotModal';
import Item3DViewerModal from '../../../items/Item3DViewerModal';

interface Props {
  offer?: OfferDTO;
  item?: ItemDTO;
  showSimilarButton?: boolean;
  onShowSimilar?: () => void;
}

const PHOTO_ALLOWED_TYPES = [
  'PISTOL', 'SMG', 'RIFLE', 'SNIPER_RIFLE', 'MACHINE_GUN',
  'SHOTGUN', 'EQUIPMENT', 'KNIFE', 'GLOVES'
];

const ItemDetails: React.FC<Props> = ({
  offer,
  item: propItem,
  showSimilarButton = false,
  onShowSimilar,
}) => {
  const item = propItem ?? offer?.item;

  const [screenshotOpen, setScreenshotOpen] = useState(false);
  const [viewer3DOpen, setViewer3DOpen] = useState(false);

  if (!item) return null;

  const { details, catalog } = item;

  const rarityKey = typeof details?.rarity === 'string' ? details.rarity : '';
  const rarityColor = RARITY_COLORS[rarityKey as RarityGrade] ?? '#374151';

  const floatValue = details?.floatValue ?? null;
  const floatPercent = floatValue === null ? 0 : Math.min(Math.max(floatValue * 100, 0), 100);

  const itemGroup = catalog?.itemGroup || (details as any)?.itemGroup;

  const handleInspectIngame = () => {
    if (details?.ingameLink) window.location.href = details.ingameLink;
  };

  const renderAttachments = () => {
    const attachments = item.attachments || [];
    if (attachments.length === 0) return null;

    return (
        <div className="absolute bottom-4 left-1.5 flex flex-col flex-nowrap gap-1 z-20">
          {attachments.map((att, idx) => (
              <Tooltip key={att.id || idx} title={att.marketHashName} arrow placement="right">
                <div className="bg-black/30 backdrop-blur-sm p-0.5 rounded border border-white/5 hover:border-white/20 transition-all duration-200">
                  <img
                      src={att.imageLink || ''}
                      alt={att.marketHashName || 'sticker'}
                      className="w-7 h-7 object-contain drop-shadow-sm"
                  />
                </div>
              </Tooltip>
          ))}
        </div>
    );
  };

  return (
      <div className="flex flex-col h-[400px] gap-3">
        {/* Header Bereich */}
        <div className="flex flex-col gap-2">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-2xl lg:text-3xl font-black tracking-tighter text-neutral-900 dark:text-white leading-none mb-2 truncate">
                {catalog.groupName || catalog.marketHashName}
              </h2>

              <div className="flex flex-wrap items-center gap-1.5">
                {catalog?.wearName && (
                    <span className="px-2.5 py-0.5 bg-blue-600/20 border border-blue-500/30 rounded text-[11px] font-black uppercase tracking-wider text-blue-600 dark:text-blue-400">
                      {catalog.wearName.replace('_', ' ')}
                    </span>
                )}
                {(catalog.isStattrak || (details as any)?.isStattrak) && (
                    <span className="px-2 py-0.5 bg-orange-600/20 border border-orange-500/30 rounded text-[10px] font-black uppercase tracking-wider text-orange-600 dark:text-orange-500">
                      StatTrak™
                    </span>
                )}
                {(catalog.isSouvenir || (details as any)?.isSouvenir) && (
                    <span className="px-2 py-0.5 bg-yellow-600/20 border border-yellow-500/30 rounded text-[10px] font-black uppercase tracking-wider text-yellow-700 dark:text-yellow-500">
                      Souvenir
                    </span>
                )}
              </div>
            </div>

            {showSimilarButton && onShowSimilar && (
                <Button
                    variant="contained"
                    size="small"
                    startIcon={<StorefrontIcon sx={{ fontSize: 16 }} />}
                    onClick={onShowSimilar}
                    className="!bg-dark-primary hover:!bg-dark-stronger !text-white !font-bold !text-xs !px-3 !py-1.5 !rounded whitespace-nowrap shrink-0 !shadow-md normal-case"
                >
                  Show Similar Items
                </Button>
            )}
          </div>

          {floatValue !== null && (
              <div className="flex items-center gap-3 bg-neutral-200/50 dark:bg-neutral-900/40 p-1.5 rounded border border-black/5 dark:border-white/5 w-fit">
                <div className="flex flex-col">
                  <div className="text-[10px] font-mono font-bold text-neutral-600 dark:text-gray-300">{floatValue.toFixed(10)}</div>
                </div>
                <div className="w-40">
                  <div
                      className="relative h-1 rounded-full w-full overflow-hidden"
                      style={{
                        background: 'linear-gradient(to right, #14532d 0%, #14532d 7%, #22c55e 7%, #22c55e 15%, #eab308 15%, #eab308 38%, #f87171 38%, #f87171 45%, #b91c1c 45%, #b91c1c 100%)',
                      }}
                  >
                    <div
                        className="absolute top-0 bottom-0 w-1 bg-white shadow-[0_0_4px_white]"
                        style={{ left: `${floatPercent}%` }}
                    />
                  </div>
                </div>
                {details?.paintSeed !== undefined && details?.paintSeed !== null && (
                    <span className="text-xs font-mono font-medium text-neutral-500 dark:text-gray-500 border-l border-black/10 dark:border-white/10 pl-3">
                      Seed: #{details.paintSeed}
                    </span>
                )}
              </div>
          )}
        </div>

        {/* Main Image Container */}
        <div
            className="relative flex-1 min-h-0 flex items-center justify-center rounded overflow-hidden bg-neutral-100 dark:bg-neutral-900/40"
            style={{
              backgroundImage: `linear-gradient(to top, ${rarityColor}66 0%, rgba(26, 29, 36, 0) 115%)`
            }}
        >
          {/* Rarity Label OBEN rechts */}
          {details?.rarity && (
              <div className="absolute top-4 right-1.5 text-right z-20">
                <p className="text-[9px] font-black uppercase tracking-[0.3em] text-neutral-400 dark:text-white/50 leading-none">Rarity Grade</p>
                <p className="text-sm font-bold uppercase tracking-widest leading-tight" style={{ color: rarityColor }}>
                  {details.rarity.replace('_', ' ')}
                </p>
                {details?.phase && (
                    <p className="text-xs font-bold text-blue-600 dark:text-blue-400 uppercase tracking-tighter italic leading-none">{details.phase}</p>
                )}
              </div>
          )}

          {/* Action Buttons UNTEN rechts */}
          <div className="absolute bottom-4 right-1.5 flex flex-col gap-2 z-20">
            {details?.ingameLink && (
                <Tooltip title="Inspect In-Game" arrow placement="left">
                  <IconButton
                      size="small"
                      onClick={handleInspectIngame}
                      className="!bg-white/80 dark:!bg-black/30 hover:!bg-neutral-200 dark:hover:!bg-black/50 !text-neutral-900 dark:!text-white !backdrop-blur-sm border border-neutral-300 dark:border-white/10 shadow-sm dark:shadow-none"
                  >
                    <ZoomInIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
            )}
            {item.itemAssetId && itemGroup && PHOTO_ALLOWED_TYPES.includes(itemGroup) && (
                <>
                  <Tooltip title="View Screenshot" arrow placement="left">
                    <IconButton
                        size="small"
                        onClick={() => setScreenshotOpen(true)}
                        className="!bg-white/80 dark:!bg-black/30 hover:!bg-neutral-200 dark:hover:!bg-black/50 !text-neutral-900 dark:!text-white !backdrop-blur-sm border border-neutral-300 dark:border-white/10 shadow-sm dark:shadow-none"
                    >
                      <CameraAltIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="360° 3D Viewer" arrow placement="left">
                    <IconButton
                        size="small"
                        onClick={() => setViewer3DOpen(true)}
                        className="!bg-white/80 dark:!bg-black/30 hover:!bg-neutral-200 dark:hover:!bg-black/50 !text-neutral-900 dark:!text-white !backdrop-blur-sm border border-neutral-300 dark:border-white/10 shadow-sm dark:shadow-none"
                    >
                      <ThreeSixtyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
            )}
          </div>

          {/* Item Image */}
          {catalog.imageLink ? (
              <img
                  src={catalog.imageLink}
                  alt={catalog.marketHashName ?? 'item'}
                  className="w-full max-w-[380px] max-h-full object-contain z-10 drop-shadow-[0_20px_50px_rgba(0,0,0,0.8)] transition-transform duration-500 hover:scale-105"
              />
          ) : (
              <Avatar sx={{ width: 100, height: 100 }} />
          )}

          {renderAttachments()}

          {/* Rarity Bottom Bar */}
          <div
              className="absolute bottom-0 left-0 right-0 h-1.5 shadow-[0_-4px_12px_rgba(0,0,0,0.5)]"
              style={{ backgroundColor: rarityColor }}
          />
        </div>

        <ItemScreenshotModal
            open={screenshotOpen}
            assetId={item.itemAssetId}
            onClose={() => setScreenshotOpen(false)}
        />

        <Item3DViewerModal
            open={viewer3DOpen}
            assetId={item.itemAssetId}
            onClose={() => setViewer3DOpen(false)}
        />
      </div>
  );
};

export default ItemDetails;
